import { useState } from 'react';

const ProgramOverview = () => {
  const [activeTab, setActiveTab] = useState(0);

  const programs = [
    {
      title: 'Behavioral Day Program',
      icon: 'ri-heart-pulse-line',
      description: 'Comprehensive support for individuals with behavioral challenges through structured activities and therapeutic interventions.',
      features: [
        'Individualized behavior support plans',
        'Social skills development',
        'Emotional regulation training',
        'Daily living skills enhancement',
        'Community integration activities'
      ]
    },
    {
      title: 'Vocational Training',
      icon: 'ri-briefcase-line',
      description: 'Practical job skills training designed to prepare individuals for meaningful employment opportunities in their communities.',
      features: [
        'Job readiness preparation',
        'Workplace skills training',
        'Resume building assistance',
        'Interview preparation',
        'Ongoing employment support'
      ]
    },
    {
      title: 'Community Integration',
      icon: 'ri-group-line',
      description: 'Programs focused on building independence and fostering meaningful connections within the broader community.',
      features: [
        'Social activities and outings',
        'Public transportation training',
        'Money management skills',
        'Recreation and leisure programs',
        'Volunteer opportunities'
      ]
    }
  ];

  return (
    <section className="py-28 px-4 bg-blue-600">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-white font-semibold text-sm uppercase tracking-wider bg-white/20 px-5 py-2 rounded-full">Our Programs</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">Comprehensive Support Programs</h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Tailored programs designed to empower individuals with developmental disabilities
          </p>
        </div>

        {/* Program Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {programs.map((program, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all cursor-pointer whitespace-nowrap ${
                activeTab === index
                  ? 'bg-teal-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
              }`}
            >
              <div className="flex items-center gap-3">
                <i className={`${program.icon} text-2xl`}></i>
                <span>{program.title}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Active Program Content */}
        <div className="bg-white rounded-2xl p-12 shadow-xl border border-gray-200">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-2xl mb-6">
                <i className={`${programs[activeTab].icon} text-teal-600 text-5xl`}></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                {programs[activeTab].title}
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {programs[activeTab].description}
              </p>
              <a 
                href="#contact" 
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-all font-semibold shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Learn More
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-md border border-gray-100">
              <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-checkbox-circle-line text-teal-600 text-xl"></i>
                </div>
                Program Features
              </h4>
              <ul className="space-y-4">
                {programs[activeTab].features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700 leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-8 mt-20">
          <div className="text-center p-8 bg-white rounded-xl shadow-md border border-gray-100">
            <div className="text-5xl font-bold text-teal-600 mb-3">100%</div>
            <div className="text-gray-700 font-semibold">Dedicated Support</div>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md border border-gray-100">
            <div className="text-5xl font-bold text-teal-600 mb-3">13+</div>
            <div className="text-gray-700 font-semibold">Years Experience</div>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md border border-gray-100">
            <div className="text-5xl font-bold text-teal-600 mb-3">3</div>
            <div className="text-gray-700 font-semibold">Regional Partners</div>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md border border-gray-100">
            <div className="text-5xl font-bold text-teal-600 mb-3">24/7</div>
            <div className="text-gray-700 font-semibold">Available Support</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramOverview;
